import { ChangeDetectorRef, Component, Inject, TemplateRef, Type } from '@angular/core';

import { Dialog, DIALOG_COMPONENT, MODAL_OPTIONS, ModalOptions } from '../../providers/modal/modal-types';

/**
 * This component should only be instatiated dynamically by the ModalService. It should not be used
 * directly in templates. See {@link ModalService.fromComponent} method for more detail.
 */
@Component({
    selector: 'vsf-modal-dialog',
    templateUrl: './modal-dialog.component.html',
    styleUrls: ['./modal-dialog.component.scss'],
})
export class ModalDialogComponent<R, T extends Dialog<R>> {
    closeModal: (result?: R) => void;
    titleTemplateRef: TemplateRef<void> | null = null;
    buttonsTemplateRef: TemplateRef<void> | null = null;

    constructor(
        @Inject(DIALOG_COMPONENT) public childComponentType: Type<object>,
        private cdr: ChangeDetectorRef,
        @Inject(MODAL_OPTIONS) public options?: ModalOptions<object>,
    ) {}

    /**
     * This callback is invoked when the childComponentType is instantiated in the
     * template by the {@link DialogComponentOutletComponent}.
     * Once we have the instance, we can set the resolveWith function and any
     * locals which were specified in the config.
     */
    onCreate(componentInstance: T) {
        componentInstance.resolveWith = (result?: R) => {
            this.closeModal(result);
        };
        if (this.options && this.options.locals) {
            const locals = this.options.locals as Partial<T>;
            (Object.keys(locals) as Array<keyof T>).forEach((key) => {
                componentInstance[key] = locals[key] as T[keyof T];
            });
        }
    }

    registerTitleTemplate(titleTemplateRef: TemplateRef<void>) {
        this.titleTemplateRef = titleTemplateRef;
        this.cdr.markForCheck();
    }

    /**
     * This should be called by the {@link DialogButtonsDirective} only
     */
    registerButtonsTemplate(buttonsTemplateRef: TemplateRef<void>) {
        this.buttonsTemplateRef = buttonsTemplateRef;
        this.cdr.markForCheck();
    }

    /**
     * Called when the modal is closed by clicking the X or the mask.
     */
    modalOpenChange(status: boolean) {
        if (status === false) {
            this.closeModal();
        }
    }
}
